import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  getReindexStatus,
  triggerVendorReindex as requestVendorReindex,
} from "../../services/reindexService";
import {
  REINDEX_POLL_MS,
  isVendorReindexRunning,
  selectVisibleVendors,
  shouldContinueReindexPolling,
  VENDOR_INDEX_WATCH_MS,
} from "../../utils/indexPollingUtils";

const VendorIndexPollingContext = createContext({
  full: null,
  vendors: [],
  isVendorReindexRunning: () => false,
  startVendorIndexPolling: () => {},
  triggerVendorReindex: async () => {},
});

export function VendorIndexPollingProvider({ children }) {
  const [full, setFull] = useState(null);
  const [vendors, setVendors] = useState([]);
  const watchUntilRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const ensurePolling = useCallback(() => {
    if (timerRef.current) {
      return;
    }
    timerRef.current = setInterval(() => {
      pollRef.current();
    }, REINDEX_POLL_MS);
  }, []);

  const pollReindexStatus = useCallback(async () => {
    try {
      const data = await getReindexStatus();
      if (!mountedRef.current) {
        return;
      }

      const fullStatus = data?.full || null;
      const allVendors = data?.vendors || [];
      setFull(fullStatus);
      setVendors(selectVisibleVendors(allVendors));

      if (
        shouldContinueReindexPolling({
          full: fullStatus,
          vendors: allVendors,
          watchUntil: watchUntilRef.current,
        })
      ) {
        ensurePolling();
        return;
      }

      watchUntilRef.current = 0;
      stopPolling();
    } catch {
      if (!mountedRef.current) {
        return;
      }
      watchUntilRef.current = 0;
      stopPolling();
    }
  }, [ensurePolling, stopPolling]);

  const pollRef = useRef(pollReindexStatus);
  pollRef.current = pollReindexStatus;

  const startVendorIndexPolling = useCallback(() => {
    watchUntilRef.current = Date.now() + VENDOR_INDEX_WATCH_MS;
    stopPolling();
    pollReindexStatus();
  }, [pollReindexStatus, stopPolling]);

  const triggerVendorReindex = useCallback(async (vendorId) => {
    await requestVendorReindex(vendorId);
    startVendorIndexPolling();
  }, [startVendorIndexPolling]);

  const checkVendorReindexRunning = useCallback(
    (vendorId) => isVendorReindexRunning(vendors, vendorId),
    [vendors],
  );

  useEffect(() => {
    mountedRef.current = true;
    pollReindexStatus();

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [pollReindexStatus, stopPolling]);

  const value = useMemo(
    () => ({
      full,
      vendors,
      isVendorReindexRunning: checkVendorReindexRunning,
      startVendorIndexPolling,
      triggerVendorReindex,
    }),
    [full, vendors, checkVendorReindexRunning, startVendorIndexPolling, triggerVendorReindex],
  );

  return (
    <VendorIndexPollingContext.Provider value={value}>
      {children}
    </VendorIndexPollingContext.Provider>
  );
}

VendorIndexPollingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useVendorIndexPolling() {
  return useContext(VendorIndexPollingContext);
}
